import { useMemo, useRef, useState } from "react";
import { addDays, addWeeks } from "date-fns";
import { resolvePath } from "mobx-state-tree";
import { isMobile } from "react-device-detect";
import { DragDropProvider } from "@dnd-kit/react";
import { DragDropManager } from "@dnd-kit/dom";
import { MAX_TIMELINE_LENGTH } from "@repo/utils";
import { getLastWorkdayOfWeek } from "@repo/structure";
import { CELL_WIDTHS } from "@utils";
import {
  useEstimateEditorSettings,
  useTableOverflowChecker,
  ScrollButtonContainer,
  useEstimateStructure,
} from "@tools";
import {
  DragDropManagerWithoutAutoscroll,
  DragDropManagerSensorSetup,
  DragDropManagerModifierSetup,
} from "@components";
import { Grid } from "@material-ui/core";
import { TimelineGrid } from "./TimelineGrid/TimelineGrid";
import { TimelineMenu } from "./TimelineMenu/TimelineMenu";
import { TimelineOverlay } from "./TimelineOverlay/TimelineOverlay";
import useStyle from "./TimelineContainer.style";
import classnames from "classnames";

const MAX_AUTOSCROLL_STEP = 25;

export const TimelineContainer = ({
  fullSectionActions,
  minimumVisibleMonths,
  setMinimumVisibleMonths,
}) => {
  const structure = useEstimateStructure();
  const { isPdfPrinterView } = useEstimateEditorSettings();

  const [ headerRef, setHeaderRef ] = useState(null);
  const [ isDragging, setIsDragging ] = useState(false);

  const previousMinimumVisibleMonths = useRef(minimumVisibleMonths);

  const manager = useMemo(() => new DragDropManager({
    sensors: DragDropManagerSensorSetup(true),
    modifiers: DragDropManagerModifierSetup(true, true),
    plugins: DragDropManagerWithoutAutoscroll,
  }), []);

  const resizeHandler = (containerWidth) => {
    if (isPdfPrinterView || isDragging) return;
    setMinimumVisibleMonths(Math.max(0, Math.floor(containerWidth / CELL_WIDTHS.VALUE.SMALL + 0.4)));
  };

  const {
    ref: viewportRef,
    onScroll,
    isTableContentOverflowing,
    scrollButtonProps,
    isScrolled,
    containerWidth,
  } = useTableOverflowChecker(headerRef, resizeHandler, undefined, true);

  const handleDragStart = () => {
    previousMinimumVisibleMonths.current = minimumVisibleMonths;
    setIsDragging(true);
  };

  const handleDragMove = ({ operation }) => {
    const container = viewportRef.current;
    const cursorX = operation?.position?.current?.x;
    if (container && Number.isFinite(cursorX)) {
      const bounds = container.getBoundingClientRect();
      const edgeZone = bounds.width * 0.2;
      let direction = 0;
      let proximity = 0;

      if (edgeZone > 0 && cursorX < bounds.left + edgeZone) {
        direction = -1;
        proximity = Math.min(1, (bounds.left + edgeZone - cursorX) / edgeZone);
      } else if (edgeZone > 0 && cursorX > bounds.right - edgeZone) {
        direction = 1;
        proximity = Math.min(1, (cursorX - (bounds.right - edgeZone)) / edgeZone);
      }

      if (direction) {
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.max(
          0,
          Math.min(
            maxScrollLeft,
            container.scrollLeft + direction * proximity * MAX_AUTOSCROLL_STEP,
          ),
        );
      }
    }

    const dragShape = operation?.shape?.current;
    const dragRight = dragShape?.left + dragShape?.width + (container?.scrollLeft ?? 0);
    const gridRight = container?.offsetWidth;
    const requiredMonths = Number.isFinite(dragRight) && Number.isFinite(gridRight) && dragRight > gridRight
      ? Math.ceil(dragRight / CELL_WIDTHS.VALUE.SMALL)
      : previousMinimumVisibleMonths.current;
    const nextMinimumVisibleMonths = Math.min(
      MAX_TIMELINE_LENGTH,
      Math.max(previousMinimumVisibleMonths.current, minimumVisibleMonths, requiredMonths),
    );
    if (nextMinimumVisibleMonths !== minimumVisibleMonths)
      setMinimumVisibleMonths(nextMinimumVisibleMonths);
  };

  const handleDragEnd = (event) => {
    const { operation, canceled } = event;
    if (!canceled) {
      const target = operation?.target;
      const targetData = target?.data;
      const dropStartDate = targetData?.date;
      const path = operation?.source?.data?.path?.replace(/\/timelines\/\w{3}/, "");
      const isStretchAction = operation?.source?.data?.action === "stretch";

      if (dropStartDate && path?.length) {
        const dropPosition = operation.shape.current.left;
        const targetPosition = target.shape?.corners?.[0]?.x ?? dropPosition;
        let targetDate = dropStartDate;

        if(dropPosition > targetPosition) {
          const periodOffset = Math.floor(((dropPosition - targetPosition) / CELL_WIDTHS.VALUE.SMALL) * (targetData.periods || 0));
          targetDate = target.type === "month"
            ? addWeeks(dropStartDate, periodOffset)
            : target.type === "week"
              ? addDays(dropStartDate, periodOffset)
              : dropStartDate;
        }

        const element = resolvePath(structure.currentTimeline, path);
        try {
          if (isStretchAction) {
            if(target.type === "month")
              targetDate = getLastWorkdayOfWeek(targetDate);
            element?.stretchCurtail?.(targetDate);
          } else
            element?.setStartDay?.(targetDate);
        } catch (error) {
          console.error(error);
        }
      }
    }
    setMinimumVisibleMonths(previousMinimumVisibleMonths.current);
    setIsDragging(false);
  };

  const classes = useStyle({ containerWidth });

  return (
    <Grid item container wrap="nowrap" direction="column" className="h-full">
      {
        !isPdfPrinterView && !isMobile && isTableContentOverflowing
          ? <ScrollButtonContainer {...scrollButtonProps} />
          : <div className={classes.scrollButtonSpacer} />
      }
      <Grid
        item container
        id="timelineContainer"
        wrap="nowrap"
        className={classnames(
          classes.root,
          isPdfPrinterView && "w-max"
        )}
      >
        <TimelineMenu
          fullSectionActions={fullSectionActions}
          addBorder={isTableContentOverflowing && isScrolled}
        />
        <DragDropProvider
          manager={manager}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={viewportRef}
            id="timelineGridContainer"
            onScroll={onScroll}
            className={classnames(
              classes.container,
              isPdfPrinterView && "w-max"
            )}
          >
            <TimelineGrid
              ref={setHeaderRef}
              fullSectionActions={fullSectionActions}
              minimumVisibleMonths={minimumVisibleMonths}
              setMinimumVisibleMonths={setMinimumVisibleMonths}
            />
            <TimelineOverlay fullSectionActions={fullSectionActions} />
          </div>
        </DragDropProvider>
      </Grid>
    </Grid>
  )
}
